import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ProgressBarWithMetas from './ProgressBarWithMetas';
import HelpButton from './HelpButton';
import { ProgressoDescritor, calcProgresso } from '../../data/boa-pratica-progresso';

interface BoaPraticaProgressoDialogProps {
  open: boolean;
  onClose: () => void;
  eixo: string;
  paciente: Record<string, any> | null;
  descritores: ProgressoDescritor[];
}

const COR_OK = '#2e7d32';
const COR_PARCIAL = '#e65100';
const COR_ZERO = '#9e9e9e';

function formatDate(dateStr: any): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

/** Chip de status (Concluida / Em andamento / Pendente) conforme atual x meta. */
const StatusChip: React.FC<{ atual: number; concluido: boolean }> = ({ atual, concluido }) => {
  const cfg = concluido
    ? { label: 'Concluida', cor: COR_OK }
    : atual > 0
      ? { label: 'Em andamento', cor: COR_PARCIAL }
      : { label: 'Pendente', cor: COR_ZERO };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: alpha(cfg.cor, 0.12), color: cfg.cor, fontWeight: 600, fontSize: '0.7rem', height: 22 }}
    />
  );
};

/** Uma sub-linha (janela do ind_d ou grupo de vacina do ind_e). */
const SubLinha: React.FC<{ label: string; ok: boolean; texto?: string }> = ({ label, ok, texto }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
    {ok ? (
      <CheckCircleIcon sx={{ fontSize: 16, color: COR_OK }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: COR_ZERO }} />
    )}
    <Typography variant="body2" sx={{ flex: 1 }}>{label}</Typography>
    {texto && (
      <Typography variant="caption" sx={{ color: ok ? COR_OK : 'text.secondary', fontWeight: 600 }}>
        {texto}
      </Typography>
    )}
  </Box>
);

const BoaPraticaCard: React.FC<{ eixo: string; paciente: Record<string, any>; desc: ProgressoDescritor }> = ({
  eixo,
  paciente,
  desc,
}) => {
  const theme = useTheme();
  const { atual, meta, concluido } = calcProgresso(paciente, desc);
  const cor = concluido ? COR_OK : atual > 0 ? COR_PARCIAL : COR_ZERO;
  const pct = meta > 0 ? (atual / meta) * 100 : 0;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1.75,
        mb: 1.25,
        bgcolor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Chip label={desc.letra} size="small" sx={{ fontWeight: 700, height: 22 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {desc.label}
        </Typography>
        <HelpButton eixo={eixo} indicador={desc.letra} size={15} />
        <StatusChip atual={atual} concluido={concluido} />
      </Box>

      {/* Barra + fracao (nao mostra fracao em existencia, que e so feito/pendente) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <ProgressBarWithMetas value={pct} color={cor} height={8} showThresholds={false} />
        </Box>
        {desc.tipo !== 'existencia' && (
          <Typography variant="body2" fontWeight={700} sx={{ color: cor, minWidth: 48, textAlign: 'right' }}>
            {atual}/{meta}
          </Typography>
        )}
      </Box>

      {/* Detalhe por tipo */}
      {desc.tipo === 'existencia' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {concluido
            ? `Realizada${desc.dateKey && paciente[desc.dateKey] ? ` em ${formatDate(paciente[desc.dateKey])}` : ''}`
            : 'Pendente'}
        </Typography>
      )}

      {desc.tipo === 'contagem' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {atual >= meta
            ? 'Meta atingida'
            : `Faltam ${meta - atual} de ${meta}`}
          {desc.dateKey && paciente[desc.dateKey] ? ` · ultimo registro: ${formatDate(paciente[desc.dateKey])}` : ''}
        </Typography>
      )}

      {desc.tipo === 'janelas' && (
        <Box sx={{ mt: 0.75 }}>
          {desc.janelas.map((j) => {
            const dt = paciente[j.dateKey];
            return <SubLinha key={j.dateKey} label={j.label} ok={!!dt} texto={dt ? formatDate(dt) : 'Pendente'} />;
          })}
        </Box>
      )}

      {desc.tipo === 'grupos' && (
        <Box sx={{ mt: 0.75 }}>
          {desc.grupos.map((g) => {
            const feitas = Number(paciente[g.countKey]) || 0;
            const ok = feitas >= g.meta;
            return <SubLinha key={g.countKey} label={g.label} ok={ok} texto={`${Math.min(feitas, g.meta)}/${g.meta}`} />;
          })}
        </Box>
      )}
    </Box>
  );
};

const BoaPraticaProgressoDialog: React.FC<BoaPraticaProgressoDialogProps> = ({
  open,
  onClose,
  eixo,
  paciente,
  descritores,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const score = paciente ? Number(paciente.score_total) || 0 : 0;
  const scoreCor = score >= 75 ? COR_OK : score >= 50 ? COR_PARCIAL : '#c62828';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 6,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {paciente?.no_cidadao || 'Detalhe'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Progresso das boas praticas
            {paciente?.idade != null ? ` · ${paciente.idade} ano(s)` : ''}
            {paciente?.equipe ? ` · ${paciente.equipe}` : ''}
          </Typography>
        </Box>
        <Chip
          label={`Score ${score}`}
          size="small"
          sx={{ bgcolor: alpha(scoreCor, 0.12), color: scoreCor, fontWeight: 700, flexShrink: 0 }}
        />
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {paciente &&
          descritores.map((desc) => (
            <BoaPraticaCard key={desc.key} eixo={eixo} paciente={paciente} desc={desc} />
          ))}
        <Divider sx={{ mt: 1, mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          As barras mostram o avanco de cada boa pratica. Uma boa pratica so pontua quando atinge a
          meta completa (ex.: 9/9 registros de peso e altura).
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoaPraticaProgressoDialog;
